// Dibuja UNA etiqueta (para un participante) según el grid configurado,
// dentro del rectángulo [x, y, etiquetaAncho, etiquetaAlto] que ya calculó
// la matemática de hoja de EtiquetasPDF.jsx (columnas/filas/páginas de la
// HOJA — eso no cambia). Reemplaza el cuerpo de dibujo hardcodeado que
// existía antes.
//
// El grid usa la misma lógica de ancho que una grilla web de 12 columnas
// (ver distribuirEnFilas en gridLogic.js): se acomodan los elementos uno
// tras otro sumando su "width" (1-12) hasta llegar a 12, momento en el que
// el siguiente pasa a la fila de abajo. Esa misma regla se aplica dos
// veces: primero para las columnas de la etiqueta, y luego para las celdas
// DENTRO de cada columna.
import logoBase64 from './logoDashport';
import { resolverTextoConVariables, resolverValorAtributo } from './attributeLogic';
import { distribuirEnFilas, LOGO_SENTINEL } from './gridLogic';

const ANCHO_MAXIMO = 12;

// jsPDF no puede resolver una URL remota por sí mismo: necesita los datos
// de la imagen ya en memoria. Se recolectan todas las URLs distintas
// usadas en el grid para precargarlas UNA sola vez antes de dibujar. El
// logo de Dashport (LOGO_SENTINEL) ya está embebido y no cuenta como URL.
export function listarImagenesDelGrid(grid) {
  const urls = new Set();
  (grid?.columns || []).forEach((columna) => {
    (columna.cells || []).forEach((cell) => {
      if (cell.content?.type === 'image' && cell.content.src && cell.content.src !== LOGO_SENTINEL) {
        urls.add(cell.content.src);
      }
    });
  });
  return Array.from(urls);
}

// Mismo patrón ya usado en este proyecto para meter una foto de Storage en
// un PDF (ver certificadoLogic.jsx): Image + crossOrigin + canvas.
export function cargarImagenComoDataURL(url) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = reject;
    img.src = url;
  });
}

function dibujarCelda(doc, cell, dato, x, y, ancho, alto, contexto, imagenesCache) {
  if (cell.content?.type === 'image') {
    const src = cell.content.src;
    const dataUrl = src === LOGO_SENTINEL ? logoBase64 : imagenesCache[src];
    if (!dataUrl) return; // imagen aún no cargada, no elegida, o falló la descarga
    const rotacion = cell.orientation === 'vertical' ? 90 : undefined;
    doc.addImage(
      dataUrl,
      'PNG',
      x + ancho * 0.1,
      y + alto * 0.1,
      ancho * 0.8,
      alto * 0.8,
      undefined,
      undefined,
      rotacion
    );
    return;
  }

  const texto = resolverTextoConVariables(cell.content?.value, (key) =>
    resolverValorAtributo(key, dato, contexto.atributosPorKey, contexto.eventSubEventsRegistry, contexto.valueRemap)
  );
  if (!texto) return;

  const centroX = x + ancho / 2;
  const centroY = y + alto / 2;
  const esVertical = cell.orientation === 'vertical';
  doc.setFontSize(cell.fontSize || 9);
  if (esVertical) {
    doc.text(texto, centroX, centroY, { align: 'center', angle: 90 });
  } else {
    const lineas = doc.splitTextToSize(texto, ancho - 4);
    doc.text(lineas, centroX, centroY, { align: 'center' });
  }
}

function dibujarColumna(doc, columna, dato, x, y, anchoColumna, altoColumna, contexto, imagenesCache) {
  const celdas = columna.cells || [];
  if (celdas.length === 0) return;
  const filasDeCeldas = distribuirEnFilas(celdas, (c) => c.width);
  const altoFilaCelda = altoColumna / filasDeCeldas.length;

  filasDeCeldas.forEach((filaCeldas, filaIdx) => {
    const totalWidthFila = filaCeldas.reduce((acc, c) => acc + (c.width || ANCHO_MAXIMO), 0) || ANCHO_MAXIMO;
    const rowY = y + filaIdx * altoFilaCelda;
    let xAcumulada = x;

    filaCeldas.forEach((cell) => {
      const anchoCelda = ((cell.width || ANCHO_MAXIMO) / totalWidthFila) * anchoColumna;
      dibujarCelda(doc, cell, dato, xAcumulada, rowY, anchoCelda, altoFilaCelda, contexto, imagenesCache);
      xAcumulada += anchoCelda;
    });
  });
}

// contexto: { atributosPorKey, eventSubEventsRegistry, valueRemap }
// imagenesCache: { [url]: dataUrl } ya precargado con cargarImagenComoDataURL
export function dibujarEtiquetaConGrid(doc, grid, dato, x, y, etiquetaAncho, etiquetaAlto, contexto, imagenesCache) {
  const columnas = grid?.columns || [];
  if (columnas.length === 0) return;

  const filasDeColumnas = distribuirEnFilas(columnas, (col) => col.width);
  const altoFilaColumnas = etiquetaAlto / filasDeColumnas.length;

  filasDeColumnas.forEach((filaColumnas, filaIdx) => {
    const totalWidthFila =
      filaColumnas.reduce((acc, c) => acc + (c.width || ANCHO_MAXIMO), 0) || ANCHO_MAXIMO;
    const rowY = y + filaIdx * altoFilaColumnas;
    let xAcumulada = x;

    filaColumnas.forEach((columna) => {
      const anchoColumna = ((columna.width || ANCHO_MAXIMO) / totalWidthFila) * etiquetaAncho;
      dibujarColumna(doc, columna, dato, xAcumulada, rowY, anchoColumna, altoFilaColumnas, contexto, imagenesCache);
      xAcumulada += anchoColumna;
    });
  });
}
